
import React from 'react';
import { Card } from "@/components/ui/card";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { AlertTriangle, Brain, Clock } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import type { PerformanceData, ErrorPatternData } from './types';
import ResponsiveChartContainer from './ResponsiveChartContainer';
import ResponsiveChartTooltip from './ResponsiveChartTooltip';

interface ErrorPatternMiningProps {
  performanceData: PerformanceData[];
}

export const ErrorPatternMining: React.FC<ErrorPatternMiningProps> = ({ performanceData }) => {
  const { isMobile, screenSize } = useIsMobile();
  
  const errorPatterns = React.useMemo(() => {
    if (!performanceData?.length) return [];
    
    // Analyze performance data to identify error patterns
    const conceptualErrors = {
      type: 'conceptual' as const,
      count: 0,
      percentage: 0,
      examples: [],
      description: 'Mistakes related to misunderstanding core concepts'
    };
    
    const carelessErrors = {
      type: 'careless' as const,
      count: 0,
      percentage: 0,
      examples: [],
      description: 'Mistakes due to oversight or hurried responses'
    };
    
    const timeBasedErrors = {
      type: 'time-bound' as const,
      count: 0,
      percentage: 0,
      examples: [],
      description: 'Mistakes made under time pressure'
    };

    // In a real implementation, we would analyze question-level data
    // For this prototype, we'll estimate based on performance patterns
    
    let totalErrors = 0;
    
    performanceData.forEach(record => {
      const errors = record.total_questions - record.correct_answers;
      totalErrors += errors;
      
      if (errors > 0) {
        // Estimate distribution based on difficulty and pattern
        // Conceptual errors more common in harder questions
        const conceptualCount = Math.round(
          errors * (record.difficulty === 'hard' ? 0.6 : record.difficulty === 'medium' ? 0.4 : 0.2)
        );
        
        // Time-based errors more common with low scores and higher question counts
        const timeBasedCount = Math.round(
          errors * (record.score < 50 && record.total_questions > 10 ? 0.5 : 0.2)
        );
        
        // Careless errors make up the rest
        const carelessCount = errors - conceptualCount - timeBasedCount;
        
        conceptualErrors.count += conceptualCount;
        timeBasedErrors.count += timeBasedCount;
        carelessErrors.count += Math.max(0, carelessCount);
        
        // Add example subject areas for each type
        if (conceptualCount > 0 && !conceptualErrors.examples.includes(record.subject)) {
          conceptualErrors.examples.push(record.subject);
        }
        
        if (timeBasedCount > 0 && !timeBasedErrors.examples.includes(record.subject)) {
          timeBasedErrors.examples.push(record.subject);
        }
        
        if (carelessCount > 0 && !carelessErrors.examples.includes(record.subject)) {
          carelessErrors.examples.push(record.subject);
        }
      }
    });
    
    // Calculate percentages
    if (totalErrors > 0) {
      conceptualErrors.percentage = Math.round((conceptualErrors.count / totalErrors) * 100);
      carelessErrors.percentage = Math.round((carelessErrors.count / totalErrors) * 100);
      timeBasedErrors.percentage = Math.round((timeBasedErrors.count / totalErrors) * 100);
    }
    
    // Limit examples to top 3 per category
    conceptualErrors.examples = conceptualErrors.examples.slice(0, 3);
    carelessErrors.examples = carelessErrors.examples.slice(0, 3);
    timeBasedErrors.examples = timeBasedErrors.examples.slice(0, 3);
    
    return [conceptualErrors, carelessErrors, timeBasedErrors].filter(pattern => pattern.count > 0);
  }, [performanceData]);

  const COLORS = ['#8B5CF6', '#F59E0B', '#EC4899'];
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontWeight="bold"
        fontSize={isMobile ? "10" : "12"}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const getPatternIcon = (type: string) => {
    switch (type) {
      case 'conceptual':
        return <Brain className="w-5 h-5 text-indigo-500" />;
      case 'careless':
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'time-bound':
        return <Clock className="w-5 h-5 text-pink-500" />;
      default:
        return null;
    }
  };

  // For very small mobile screens, switch to vertical stacking
  const useStackedLayout = isMobile && screenSize === 'xs';

  if (!errorPatterns.length) {
    return (
      <Card className={`${isMobile ? 'p-3' : 'p-6'} bg-gradient-to-br from-indigo-50 to-white border border-slate-100 shadow-sm`}>
        <h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold mb-4 flex items-center gap-2 bg-clip-text text-transparent bg-gradient-to-r from-purple-700 to-indigo-500`}>
          <AlertTriangle className="w-5 h-5 text-purple-500" />
          Error Pattern Mining
        </h2>
        <div className="h-[200px] sm:h-[300px] flex items-center justify-center">
          <p className="text-gray-500">Not enough data to analyze error patterns.</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className={`${isMobile ? 'p-3' : 'p-6'} bg-gradient-to-br from-indigo-50 to-white border border-slate-100 shadow-sm`}>
      <h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold mb-2 sm:mb-4 flex items-center gap-2 bg-clip-text text-transparent bg-gradient-to-r from-purple-700 to-indigo-500`}>
        <AlertTriangle className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} text-purple-500`} />
        Error Pattern Mining
      </h2>
      
      <div className={`flex ${useStackedLayout ? 'flex-col' : 'flex-col lg:flex-row'} gap-4`}>
        <div className={`${useStackedLayout ? 'w-full h-[200px]' : (isMobile ? 'w-full h-[220px]' : 'w-1/2 h-[300px]')}`}>
          <ResponsiveChartContainer 
            config={{
              margin: { top: 5, right: 5, left: 5, bottom: 5 }
            }}
            mobileHeight={200}
            desktopHeight={300}
            mobilePadding="px-0"
            desktopPadding="px-0"
          >
            <PieChart>
              <Pie
                data={errorPatterns}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomizedLabel}
                outerRadius={isMobile ? 70 : 100}
                innerRadius={isMobile ? 30 : 40}
                fill="#8884d8"
                dataKey="percentage"
                nameKey="type"
              >
                {errorPatterns.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <ResponsiveChartTooltip 
                formatter={(value, name) => {
                  switch (name) {
                    case 'conceptual':
                      return [`${value}%`, 'Conceptual Errors'];
                    case 'careless':
                      return [`${value}%`, 'Careless Errors'];
                    case 'time-bound':
                      return [`${value}%`, 'Time-bound Errors'];
                    default:
                      return [value, name];
                  }
                }}
                contentStyle={{ 
                  backgroundColor: 'white', 
                  borderRadius: '8px', 
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  border: '1px solid #E5E7EB'
                }}
              />
            </PieChart>
          </ResponsiveChartContainer>
        </div>
        
        <div className={`${useStackedLayout ? 'w-full' : (isMobile ? 'w-full' : 'w-1/2')} space-y-2 sm:space-y-4 overflow-auto`}>
          {errorPatterns.map((pattern, index) => (
            <div key={pattern.type} className={`p-2 sm:p-4 rounded-lg border bg-white ${useStackedLayout ? 'max-h-24 overflow-y-auto' : ''}`}>
              <div className="flex items-start gap-2 sm:gap-3">
                <div className={`p-1 sm:p-2 rounded-full bg-gradient-to-br from-indigo-50 to-white ${isMobile ? 'mt-0.5' : ''}`}>
                  {getPatternIcon(pattern.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className={`font-semibold ${isMobile ? 'text-sm' : ''} flex items-center gap-2 flex-wrap`}>
                    <span className="truncate">{pattern.type.charAt(0).toUpperCase() + pattern.type.slice(1)} Errors</span>
                    <span className={`${isMobile ? 'text-xs px-1.5 py-0.5' : 'text-sm px-2 py-1'} bg-slate-100 rounded-full inline-flex`}>
                      {pattern.percentage}%
                    </span>
                  </h3>
                  <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-600 mt-0.5 sm:mt-1`}>{pattern.description}</p>
                  
                  {pattern.examples.length > 0 && (
                    <div className={`mt-1 sm:mt-2 ${useStackedLayout ? 'hidden' : ''}`}>
                      <p className={`${isMobile ? 'text-xs' : 'text-xs'} text-gray-500`}>Common subjects:</p>
                      <div className="flex flex-wrap gap-1 mt-0.5 sm:mt-1">
                        {pattern.examples.map(example => (
                          <span 
                            key={example} 
                            className={`${isMobile ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-1'} bg-slate-50 rounded-full border border-slate-200`}
                          >
                            {example}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className={`mt-1 sm:mt-3 ${useStackedLayout ? 'hidden sm:block' : ''}`}>
                    <h4 className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium`}>Recommendation:</h4>
                    <p className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-gray-600`}>
                      {pattern.type === 'conceptual' && 'Focus on revising fundamental concepts in these subjects'}
                      {pattern.type === 'careless' && 'Practice careful review of answers before submission'}
                      {pattern.type === 'time-bound' && 'Work on time management through timed practice sessions'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};
